import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, themes, getThemeById, getDefaultTheme } from '@/styles/themes';

interface ThemeContextType {
  currentTheme: Theme;
  setTheme: (themeId: string) => void;
  loadThemeFromPreferences: (themeId: string) => void;
  availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(getDefaultTheme());
  const [isInitialized, setIsInitialized] = useState(false);

  // Load theme from localStorage on mount (fallback)
  useEffect(() => {
    if (!isInitialized) {
      const savedThemeId = localStorage.getItem('tmos-theme');
      if (savedThemeId) {
        const savedTheme = getThemeById(savedThemeId);
        if (savedTheme) {
          setCurrentTheme(savedTheme);
        } else {
          // If saved theme doesn't exist, use default
          setCurrentTheme(getDefaultTheme());
          localStorage.setItem('tmos-theme', 'tmos-light');
        }
      } else {
        // Use T-MOS Light as default theme
        setCurrentTheme(getDefaultTheme());
        localStorage.setItem('tmos-theme', 'tmos-light');
      }
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // Function to load theme from user preferences (called from UserProfilePage)
  // This takes priority over localStorage
  const loadThemeFromPreferences = (themeId: string) => {
    const theme = getThemeById(themeId);
    if (theme) {
      setCurrentTheme(theme);
      // Sync with localStorage
      localStorage.setItem('tmos-theme', themeId);
    }
  };

  // Apply theme to CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    
    // Set CSS custom properties for the current theme
    root.style.setProperty('--color-primary-bg', currentTheme.colors.primary.background);
    root.style.setProperty('--color-primary-text', currentTheme.colors.primary.text);
    root.style.setProperty('--color-secondary-bg', currentTheme.colors.secondary.background);
    root.style.setProperty('--color-secondary-text', currentTheme.colors.secondary.text);
    root.style.setProperty('--color-accent-bg', currentTheme.colors.accent.background);
    root.style.setProperty('--color-accent-text', currentTheme.colors.accent.text);
    root.style.setProperty('--color-surface-bg', currentTheme.colors.surface.background);
    root.style.setProperty('--color-surface-text', currentTheme.colors.surface.text);
    root.style.setProperty('--color-border', currentTheme.colors.border);
    root.style.setProperty('--color-shadow', currentTheme.colors.shadow);
    root.style.setProperty('--color-success', currentTheme.colors.success);
    root.style.setProperty('--color-warning', currentTheme.colors.warning);
    root.style.setProperty('--color-error', currentTheme.colors.error);
    root.style.setProperty('--color-info', currentTheme.colors.info);
  }, [currentTheme]);

  const setTheme = (themeId: string) => {
    const newTheme = getThemeById(themeId);
    if (newTheme) {
      setCurrentTheme(newTheme);
      localStorage.setItem('tmos-theme', themeId);
    }
  };

  const value: ThemeContextType = {
    currentTheme,
    setTheme,
    loadThemeFromPreferences,
    availableThemes: themes,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 


